import { useState } from "react";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Clock, RefreshCw } from "lucide-react";

const STATUS = {
  pending_review: { label: "Pending review", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock },
  accepted: { label: "Accepted", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  rejected: { label: "Rejected", cls: "bg-red-50 text-red-600 border-red-200", Icon: XCircle },
  rejected_resubmission_requested: { label: "Rejected · resubmission requested", cls: "bg-red-50 text-red-600 border-red-200", Icon: RefreshCw },
};

export const reviewStatusLabel = (status) =>
  (STATUS[status || "pending_review"] || STATUS.pending_review).label;

export const FormReviewBadge = ({ status, testId }) => {
  const s = STATUS[status || "pending_review"] || STATUS.pending_review;
  const { Icon } = s;
  return (
    <span data-testid={testId || "form-review-badge"}
      className={`text-[9px] font-semibold px-2 py-0.5 rounded-full border flex items-center gap-1 ${s.cls}`}>
      <Icon className="w-2.5 h-2.5" /> {s.label}
    </span>
  );
};

const reviewedOn = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

const REJECTION_REASONS = [  "Missing information",
  "Missing signature",
  "Incorrect details",
  "Medical history section incomplete",
  "Wrong form completed",
  "Needs clarification",
  "Duplicate / no resubmission needed",
];

export const FormReviewPanel = ({ submission, onReviewed }) => {
  const [showReject, setShowReject] = useState(false);
  const [note, setNote] = useState("");
  const [requestResubmission, setRequestResubmission] = useState(true);
  const [patientMessage, setPatientMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const status = submission?.review_status || "pending_review";
  const reviewed = status !== "pending_review";

  const submitReview = async (decision) => {
    setError("");
    if (decision === "rejected" && !note.trim()) {
      setError("Please add a note explaining why this form is rejected.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/forms/submissions/${submission.id}/review`, {
        decision,
        review_note: note.trim() || null,
        request_resubmission: decision === "rejected" ? requestResubmission : false,
        patient_message: decision === "rejected" && requestResubmission ? (patientMessage.trim() || null) : null,
      });
      setShowReject(false);
      onReviewed?.(data.submission, data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not save the review. Please try again.");
    }
    setBusy(false);
  };

  return (
    <div className="border border-[#1A1B1A]/[0.08] rounded-xl bg-[#FAF9F6] p-4" data-testid="form-review-panel">
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-xs font-semibold text-[#1A1B1A]">Form review</p>
        <FormReviewBadge status={status} testId="form-review-status-badge" />
      </div>

      {reviewed ? (
        <div className="space-y-1" data-testid="form-review-summary">
          <p className="text-[10px] text-[#757675]">
            {STATUS[status]?.label || "Reviewed"}
            {submission.reviewed_at ? ` on ${reviewedOn(submission.reviewed_at)}` : ""}
          </p>
          {submission.review_note_internal && (
            <p className="text-[11px] text-[#4A4B4A] bg-white border border-[#1A1B1A]/[0.06] rounded-lg px-2.5 py-1.5" data-testid="form-review-note">
              {submission.review_note_internal}
            </p>
          )}
          {submission.resubmission_requested && (
            <p className="text-[10px] text-[#496275]" data-testid="form-resubmission-info">
              A fresh form request was created for the patient
              {submission.resubmission_form_request_id ? ` (${submission.resubmission_form_request_id.substring(0, 8)})` : ""}
              {submission.email_log_id ? " · email logged" : " · no email sent"}
            </p>
          )}
          <p className="text-[9px] text-[#757675] pt-1">This submission remains stored as a read-only record.</p>
        </div>
      ) : showReject ? (
        <div className="space-y-2" data-testid="form-reject-form">
          <div>
            <label className="text-[9px] font-semibold text-[#757675] uppercase tracking-wider block mb-1">Reason (internal, required)</label>
            <select value={REJECTION_REASONS.includes(note) ? note : ""} onChange={e => setNote(e.target.value)}
              data-testid="reject-reason-select"
              className="w-full text-[11px] px-2 py-1.5 rounded-lg border border-[#1A1B1A]/10 bg-white mb-1.5">
              <option value="">Choose a reason or type your own…</option>
              {REJECTION_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
              data-testid="reject-note-input"
              placeholder="Explain why this form is being rejected…"
              className="w-full text-[11px] px-2.5 py-1.5 rounded-lg border border-[#1A1B1A]/10 bg-white resize-y" />
          </div>
          <label className="flex items-center gap-2 text-[11px] text-[#4A4B4A]">
            <input type="checkbox" checked={requestResubmission} onChange={e => setRequestResubmission(e.target.checked)}
              data-testid="request-resubmission-checkbox" className="rounded border-[#1A1B1A]/20 text-[#205B5A]" />
            Request patient to resubmit
          </label>
          {requestResubmission && (
            <textarea value={patientMessage} onChange={e => setPatientMessage(e.target.value)} rows={2}
              data-testid="patient-message-input"
              placeholder="Optional message to the patient (kept brief and non-clinical)…"
              className="w-full text-[11px] px-2.5 py-1.5 rounded-lg border border-[#1A1B1A]/10 bg-white resize-y" />
          )}
          {error && <p className="text-[10px] text-red-600" data-testid="form-review-error">{error}</p>}
          <div className="flex gap-2">
            <button onClick={() => submitReview("rejected")} disabled={busy} data-testid="confirm-reject-btn"
              className="flex-1 text-[11px] font-medium px-3 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 disabled:opacity-60">
              {busy ? "Saving…" : "Confirm rejection"}
            </button>
            <button onClick={() => { setShowReject(false); setError(""); }} data-testid="cancel-reject-btn"
              className="text-[11px] font-medium px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-[#4A4B4A] hover:bg-white">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-[10px] text-[#757675]" data-testid="form-review-pending-text">
            This form has been submitted and is awaiting staff review.
          </p>
          <p className="text-[10px] text-[#757675]">Check the submitted answers, then record your decision.</p>
          {error && <p className="text-[10px] text-red-600" data-testid="form-review-error">{error}</p>}
          <div className="flex gap-2">
            <button onClick={() => submitReview("accepted")} disabled={busy} data-testid="accept-form-btn"
              className="flex-1 text-[11px] font-medium px-3 py-2 rounded-lg bg-[#205B5A] text-white hover:bg-[#174544] disabled:opacity-60">
              {busy ? "Saving…" : "Accept form"}
            </button>
            <button onClick={() => setShowReject(true)} data-testid="reject-form-btn"
              className="flex-1 text-[11px] font-medium px-3 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50">
              Reject form
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
